// Client-side mirror of the backend vote state machine (controllers/questions.js
// `answerQuestion`). Given a question's current viewer state and the vote just
// cast, it returns the new state so we can patch the RTK cache optimistically
// instead of refetching the whole feed on every vote.
//
// `answered`: true = voted "yes" (like), false = voted "no" (dislike), null = no vote.
// Transitions (identical to the server's countDelta logic):
//   - no vote        -> cast: set answered, +1 to that side
//   - same vote      -> toggle off: clear answered, -1 from that side
//   - opposite vote  -> flip: move the vote, +1 new side / -1 old side
// Counts are clamped at 0 to match the server's `$max: [0, ...]` guard, so a
// stale cache can never render a negative tally.

export interface VoteState {
  answered: boolean | null;
  likes: number;
  dislikes: number;
}

const clamp0 = (n: number): number => (n < 0 ? 0 : n);

export const applyVote = (current: VoteState, answer: boolean): VoteState => {
  const { answered, likes, dislikes } = current;

  // No existing vote — cast a fresh one.
  if (answered === null || answered === undefined) {
    return answer
      ? { answered: true, likes: clamp0(likes + 1), dislikes }
      : { answered: false, likes, dislikes: clamp0(dislikes + 1) };
  }

  // Same vote again — toggle it off.
  if (answered === answer) {
    return answer
      ? { answered: null, likes: clamp0(likes - 1), dislikes }
      : { answered: null, likes, dislikes: clamp0(dislikes - 1) };
  }

  // Opposite vote — flip sides.
  return answer
    ? { answered: true, likes: clamp0(likes + 1), dislikes: clamp0(dislikes - 1) }
    : { answered: false, likes: clamp0(likes - 1), dislikes: clamp0(dislikes + 1) };
};

// --- Recent-vote overlay -----------------------------------------------------
// The feed is intentionally NOT refetched on a vote (that's the perf win), so the
// only thing keeping a voted card correct is the optimistic cache patch. But an
// UNRELATED full-feed refetch (focus/reconnect, or another mutation invalidating
// the broad 'Questions' tag, or pagination) can resolve AFTER the vote settled
// and write server data that doesn't yet include the vote — reverting the card
// with nothing to correct it. The one-shot post-settle repair can't catch a
// clobber that lands later.
//
// So we keep a short-lived record of the DIRECTION of each recent vote and
// re-apply it to any feed/detail response (via transformResponse), independent of
// timing. We store only the direction, never frozen counts: on fresh data we add
// our ±1 on top of whatever the server returned, so concurrent votes by others
// are preserved. Once the server's own data reflects the vote, the record is
// dropped.

interface RecentVote {
  answer: boolean;
  ts: number;
}

// Long enough to outlast an in-flight refetch, short enough that a genuinely
// reverted/!changed server state isn't masked indefinitely.
const RECENT_VOTE_TTL_MS = 20_000;

const recentVotes = new Map<string, RecentVote>();

export const recordRecentVote = (questionId: string, answer: boolean): void => {
  recentVotes.set(questionId, { answer, ts: Date.now() });
};

export const clearRecentVote = (questionId: string): void => {
  recentVotes.delete(questionId);
};

// Test-only: clear all recorded votes so cases don't leak overlay state into
// each other (the map is a process-global, which is correct for the app).
export const resetRecentVotes = (): void => {
  recentVotes.clear();
};

type OverlayTarget = VoteState & { questionId: string };

// Re-apply a still-pending recent vote on top of freshly-fetched server data.
export const applyRecentVote = <T extends OverlayTarget>(q: T): T => {
  const recent = recentVotes.get(q.questionId);
  if (!recent) return q;
  if (Date.now() - recent.ts > RECENT_VOTE_TTL_MS) {
    recentVotes.delete(q.questionId);
    return q;
  }
  // Server already reflects the vote — overlay has served its purpose.
  if (q.answered === recent.answer) {
    recentVotes.delete(q.questionId);
    return q;
  }
  // Server data predates the vote: re-apply its effect on the CURRENT counts
  // (applyVote casts from null or flips from the opposite — either way the
  // result has answered === recent.answer, so other users' count moves survive).
  return { ...q, ...applyVote(q, recent.answer) };
};

export const applyRecentVotes = <T extends OverlayTarget>(list: T[]): T[] => list.map(applyRecentVote);
